<template>
    <vx-modal :visible="props.visible" @update:visible="emit('update:visible', $event)" class="max-w-lg">
        <template v-slot:header>Add Hosted Number</template>

        <vx-tabs v-model="vendorTab" size="lg" aria-label="Vendor" class="mb-4">
            <vx-tab data-test="host-tab-twilio" value="twilio">Twilio</vx-tab>
            <vx-tab data-test="host-tab-bandwidth" value="bandwidth">Bandwidth</vx-tab>
        </vx-tabs>

        <div v-if="vendorTab === 'twilio'" class="p-1 mb-3">
            <div class="text-sm pb-4">Enter the Twilio phone number ID of the hosted number.</div>
            <vx-input
                data-test="vendor-indentity"
                type="text"
                v-model="vendorId"
                size="lg"
                placeholder="PN5f1da2c3sedfabc1234ae12fv11fe12a"
            >
            </vx-input>
        </div>

        <div v-else class="p-1 mb-3">
            <div class="text-sm pb-4">Enter the phone number to host from Bandwidth.</div>
            <vx-input
                data-test="bandwidth-number"
                type="text"
                v-model="bandwidthNumber"
                size="lg"
                placeholder="(555) 555-0123"
            >
            </vx-input>
        </div>

        <div class="p-1 mb-3">
            <vx-label size="md">Requested by</vx-label>
            <div class="text-sm pb-4">
                The customer request or ticket this number is hosted on. It is kept with the voice change log.
            </div>
            <vx-input data-test="requested-by" type="text" v-model="requestedBy" size="lg" maxlength="250"> </vx-input>
        </div>

        <div class="p-1 mb-3">
            <span v-for="error in errors" :key="error" data-test="errors" class="text-xs text-rose-500">
                {{ error }}
            </span>
        </div>

        <div class="flex space-x-2 justify-center">
            <vx-button
                @click="submit"
                data-test="submit-button"
                type="button"
                size="lg"
                color="primary"
                class="grow"
                :loading="loading"
            >
                Save
            </vx-button>
        </div>
    </vx-modal>
</template>
<script setup>
import { ref, watch } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { VxButton, VxInput, VxLabel, VxModal, VxTabs, VxTab } from '@voxie/frontend-components';
import { adminPhoneNumbersClient } from '~/services';
import { e164Format, isValidPhoneNumber, phoneCountry } from '~/utils/phoneNumber';
import { HTTP_BAD_REQUEST, HTTP_UNPROCESSABLE_ENTITY } from '~/support/HttpStatuses';

const $toasted = useToasted();

const props = defineProps({
    visible: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:visible', 'save']);

const teamId = Spark.state.currentTeam.id;
const loading = ref(false);
const vendorTab = ref('twilio');
const vendorId = ref(undefined);
const bandwidthNumber = ref(undefined);
const requestedBy = ref(undefined);
const errors = ref([]);

watch(
    () => props.visible,
    (visible) => {
        if (!visible) {
            vendorTab.value = 'twilio';
            vendorId.value = undefined;
            bandwidthNumber.value = undefined;
            requestedBy.value = undefined;
            errors.value = [];
        }
    }
);

watch(vendorTab, () => {
    errors.value = [];
});

const submit = async () => {
    if (loading.value) {
        return;
    }

    errors.value = [];

    let payload;
    if (vendorTab.value === 'bandwidth') {
        const formatted = e164Format(bandwidthNumber.value);
        if (!formatted || !isValidPhoneNumber(bandwidthNumber.value)) {
            errors.value.push('Enter a valid phone number.');
            return;
        }
        payload = {
            vendor: 'bandwidth',
            phone_number: formatted,
            country: phoneCountry(formatted) === 'CA' ? 'CA' : 'US',
        };
    } else {
        if (!vendorId.value?.startsWith('PN')) {
            errors.value.push('Vendor ID should start with PN');
            return;
        }
        payload = {
            vendor_identity: vendorId.value,
        };
    }

    loading.value = true;

    try {
        await adminPhoneNumbersClient.host(teamId, { ...payload, requested_by: requestedBy.value });
        emit('save');
    } catch (error) {
        const statusCode = error?.response?.status;
        if (statusCode === HTTP_UNPROCESSABLE_ENTITY) {
            const serverErrors = error?.response?.data?.errors;
            if (serverErrors) {
                errors.value = Object.values(serverErrors).flat();
            }
        } else if (statusCode === HTTP_BAD_REQUEST) {
            const serverError = error?.response?.data?.message;
            if (serverError) {
                errors.value.push(serverError);
            }
        } else {
            console.error(error);
            $toasted.global.platform_error();
        }
    } finally {
        loading.value = false;
    }
};
</script>
